/**
 * Обязательный платёж — один лист для настройки, «+» в «Платежах» и правки
 * (22.10.2026, макет docs/design/forms-2026-10-22).
 *
 * Прежде в настройке были коробки и выпадающие списки браузера, не было счёта,
 * и платёж сохранялся без проверки даты. В приложении сверху стояли метки «Что
 * это», а «С какого счёта» — строкой: смесь двух обликов. Теперь лист один и
 * устроен как лист кредита: сумма крупно, остальное строками; дата и повтор —
 * строкой «Когда», выбор — в своём листе.
 */
import { useState, type ReactNode } from 'react';
import { ZERO, type Account } from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { BigAmount, CardAccount, CardInput, CardToggle } from './card-fields.js';
import { ScheduleNote, ScheduleRows, scheduleReady, type ScheduleDraft } from './schedule-fields.js';
import { newLoan, type DLoan } from './debt-sheet.js';
import { Icon } from './icon.js';
import { SERVICES } from './services.js';
import { parseAmountInput, todayPd } from './store.js';

export interface ObligationDraft {
  name: string;
  amount: string;
  /** Сумма плавает: в расчёте — верхняя граница. */
  variable: boolean;
  sched: ScheduleDraft;
  /** С какого счёта; нет — первый тратимый. */
  accountId?: string;
}

// Ипотека, кредит и рассрочка — не подсказки суммы: у них есть остаток, ставка
// и срок, поэтому они заводятся кредитом.
export const PRESETS = ['Аренда', 'ЖКХ', 'Связь', 'Садик', 'Няня', 'Подписки', 'Страховка'];
/** У этих платежей сумма меняется месяц к месяцу, и это стоит знать сразу. */
export const VARIABLE_PRESETS = new Set(['ЖКХ', 'Связь']);

/** Долги — метками в начале «Что это»: открывают лист кредита. */
const LOAN_CHIPS: Array<[string, () => DLoan]> = [
  ['Кредит', () => newLoan('Ипотека', 'amortizing', '12')],
  ['Рассрочка', () => newLoan('Рассрочка', 'installment', '0')],
  ['Личный долг', () => newLoan('', 'private', '0')],
];

export function ObligationSheet({
  init, isNew, title, accounts, onLoan, tip, extra, onSave, onDelete, deleteConfirm, onClose, services = false,
}: {
  /** Новая подписка (04.11.2026): сверху сервисы значками — тап ставит название. */
  services?: boolean;
  init: ObligationDraft;
  isNew: boolean;
  title?: string;
  /** Счета для «С какого счёта». Нет — строки нет. */
  accounts?: Account[];
  /** «Что это» сверху: долг уходит в лист кредита, заготовка ставит название. */
  onLoan?: (init: DLoan) => void;
  tip?: (v: ObligationDraft) => ReactNode;
  /** Правка: «Оплачен за …», отложенное, «Было → станет». */
  extra?: (v: ObligationDraft) => ReactNode;
  onSave: (v: ObligationDraft) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  deleteConfirm?: string;
  onClose: () => void;
}) {
  const today = todayPd();
  const [v, setV] = useState<ObligationDraft>(init);
  const [busy, setBusy] = useState(false);
  const on = <K extends keyof ObligationDraft>(k: K, x: ObligationDraft[K]) => setV((p) => ({ ...p, [k]: x }));

  const ready = !busy && v.name.trim() !== '' && parseAmountInput(v.amount) > ZERO && scheduleReady(v.sched)
    && (!accounts || Boolean(v.accountId));
  const head = tip?.(v);

  return (
    <SheetWrap title={title ?? (isNew ? 'Новый платёж' : v.name || 'Платёж')} onClose={onClose} className="rcpt"
      {...(head ? { tip: head } : {})}>
      <div className="rcform">
        {onLoan && isNew && (
          <>
            <div className="rchead">Что это</div>
            <div className="fx-quick">
              {LOAN_CHIPS.map(([label, make]) => (
                <button key={label} type="button" className="chip" onClick={() => onLoan(make())}>
                  {label} <Icon name="arrow-right" size={10} /></button>
              ))}
              {PRESETS.map((p) => (
                <button key={p} type="button" className={`chip${v.name === p ? ' on' : ''}`}
                  onClick={() => setV((x) => ({ ...x, name: p, ...(VARIABLE_PRESETS.has(p) ? { variable: true } : {}) }))}>{p}</button>
              ))}
            </div>
          </>
        )}

        {services && isNew && (
          <>
            <div className="rchead">Какой сервис</div>
            <div className="svcgrid">
              {SERVICES.filter((x) => x.id !== 'skoka').map((x) => (
                <button key={x.id} type="button" className={`svctile${v.name === x.name ? ' on' : ''}`} aria-pressed={v.name === x.name}
                  onClick={() => on('name', x.name)}>
                  <span className="subico"><img src={x.src} alt="" width={36} height={36} /></span>
                  <span>{x.name}</span>
                </button>
              ))}
            </div>
            <p className="note">Нет в списке — впиши название ниже.</p>
          </>
        )}

        <BigAmount label="Сколько за раз" value={v.amount} onChange={(x) => on('amount', x)} />

        <div className="rccard rcsel">
          <CardInput label="Название" value={v.name} onChange={(x) => on('name', x)} placeholder={services ? 'Кинопоиск' : 'Аренда'} width={170} />
          <ScheduleRows value={v.sched} onChange={(x) => on('sched', x)} today={today} />
          {accounts && accounts.length > 0 && (
            <CardAccount label="С какого счёта" pickerTitle="Платить со счёта" accounts={accounts}
              value={v.accountId ?? ''} onChange={(id) => on('accountId', id)} />
          )}
        </div>
        <ScheduleNote value={v.sched} />

        <div className="rccard rcsel">
          <CardToggle label="Сумма плавает" sub="ЖКХ, связь: в расчёте — верхняя граница"
            checked={v.variable} onChange={(x) => on('variable', x)} />
        </div>

        {extra?.(v)}

        <button className="btn pri" disabled={!ready}
          onClick={async () => {
            setBusy(true);
            try { await onSave({ ...v, name: v.name.trim() }); } finally { setBusy(false); }
          }}>{isNew ? 'Добавить' : 'Сохранить'}</button>
        {onDelete && !isNew && (
          <button className="textact danger" disabled={busy}
            onClick={() => { if (confirm(deleteConfirm ?? 'Удалить платёж?')) void onDelete(); }}>Удалить</button>
        )}
      </div>
    </SheetWrap>
  );
}
